import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Camera, ImagePlus, UploadCloud } from 'lucide-react';

import { t, tn } from '../lib/i18n';
import { ACCEPTED_EXTENSIONS, ACCEPTED_MIME_TYPES } from '../lib/types';
import { useAppStore } from '../store';

interface DropzoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

function getExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  if (idx === -1) return '';
  return name.slice(idx).toLowerCase();
}

function isAccepted(file: File): boolean {
  const mime = file.type.toLowerCase();
  if ((ACCEPTED_MIME_TYPES as readonly string[]).includes(mime)) return true;
  const ext = getExtension(file.name);
  return (ACCEPTED_EXTENSIONS as readonly string[]).includes(ext);
}

export function Dropzone({ onFiles, disabled = false }: DropzoneProps) {
  const lang = useAppStore((s) => s.lang);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const handleFiles = useCallback(
    (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      if (files.length === 0) return;
      setError(null);

      const accepted: File[] = [];
      const rejected: string[] = [];
      for (const file of files) {
        if (!isAccepted(file)) {
          rejected.push(file.name);
          continue;
        }
        accepted.push(file);
      }

      const acceptedExts = ACCEPTED_EXTENSIONS.join(', ');
      if (rejected.length > 0 && accepted.length === 0) {
        setError(
          tn(
            lang,
            rejected.length,
            'dropzone.unsupported.one',
            'dropzone.unsupported.many',
            { accepted: acceptedExts },
          ),
        );
      } else if (rejected.length > 0) {
        // Mixed: some accepted, some not. Report what was skipped but still
        // process the valid ones.
        setError(
          tn(
            lang,
            rejected.length,
            'dropzone.skipped.one',
            'dropzone.skipped.many',
          ),
        );
      }

      if (accepted.length > 0) onFiles(accepted);
    },
    [onFiles, lang],
  );

  // Paste-from-clipboard listener.
  useEffect(() => {
    if (disabled) return;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.files;
      if (!items || items.length === 0) return;
      e.preventDefault();
      handleFiles(items);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [handleFiles, disabled]);

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    handleFiles(files);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    handleFiles(files);
    // Reset so picking the same file again still fires change.
    e.target.value = '';
  };

  const baseClasses =
    'group relative flex flex-col items-center justify-center w-full rounded-2xl border-2 px-4 py-10 text-center transition-all duration-200 sm:px-6 sm:py-14';
  const stateClasses = disabled
    ? 'border-dashed border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed dark:border-slate-700 dark:bg-slate-900'
    : isDragging
      ? 'border-solid border-indigo-500 bg-indigo-50 scale-[1.01] shadow-lg shadow-indigo-500/10 dark:border-indigo-400 dark:bg-indigo-500/10 dark:shadow-indigo-400/10'
      : 'border-dashed border-slate-300 bg-white hover:border-indigo-400 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-indigo-500 dark:hover:bg-slate-800/50';

  const Icon = isDragging ? ImagePlus : UploadCloud;

  return (
    <div className="space-y-2">
      <div
        role="region"
        aria-label={t(lang, 'dropzone.regionLabel')}
        aria-disabled={disabled}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        className={`${baseClasses} ${stateClasses}`}
      >
        <span
          className={`mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full transition-colors sm:h-14 sm:w-14 ${
            isDragging
              ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300'
              : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-50 group-hover:text-indigo-600 dark:bg-slate-800 dark:text-slate-400 dark:group-hover:bg-indigo-500/10 dark:group-hover:text-indigo-300'
          }`}
        >
          <Icon className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
        </span>
        <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
          {isDragging
            ? t(lang, 'dropzone.dropToUpload')
            : t(lang, 'dropzone.dropHere')}
        </p>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {t(lang, 'dropzone.subtitle')}
        </p>
        {/*
         * Two file inputs: a multi-file picker that triggers the OS gallery
         * / file dialog, and a single-file `capture="environment"` input
         * that mobile browsers route directly to the rear camera. Desktop
         * browsers without a webcam typically fall back to the file
         * dialog for the camera button — that's fine, both paths land
         * the file in the same handleFiles flow. Visually hidden inputs
         * with sr-only classes so the surrounding <label> drives them
         * via click-through (preserves keyboard focus + a-11y semantics
         * that `hidden` would break).
         */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <label
            className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium shadow-sm transition-all ${
              disabled
                ? 'cursor-not-allowed bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-500'
                : 'cursor-pointer bg-indigo-600 text-white shadow-indigo-500/20 hover:bg-indigo-700 hover:shadow-md focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus-within:ring-offset-slate-950'
            }`}
          >
            <Camera className="h-4 w-4" aria-hidden="true" />
            {t(lang, 'dropzone.takePhoto')}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              disabled={disabled}
              onChange={onInputChange}
              aria-label={t(lang, 'dropzone.takePhotoAria')}
              className="sr-only"
            />
          </label>
          <label
            className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium shadow-sm transition-all ${
              disabled
                ? 'cursor-not-allowed bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-500'
                : 'cursor-pointer border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:shadow-md focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:focus-within:ring-offset-slate-950'
            }`}
          >
            {t(lang, 'dropzone.chooseFile')}
            <input
              ref={inputRef}
              type="file"
              multiple
              disabled={disabled}
              accept={[...ACCEPTED_MIME_TYPES, ...ACCEPTED_EXTENSIONS].join(',')}
              onChange={onInputChange}
              aria-label={t(lang, 'dropzone.chooseFileAria')}
              className="sr-only"
            />
          </label>
        </div>
        <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
          {t(lang, 'dropzone.accepted', {
            extensions: ACCEPTED_EXTENSIONS.join(', '),
          })}
        </p>
      </div>
      {error !== null && (
        <p
          role="alert"
          className="flex items-center gap-1.5 text-sm text-red-700 dark:text-red-400"
        >
          <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
